/**
 * 目标表单卡（新建 / 编辑）—— I 域 · `DR-050`
 *
 * 依据：`AC-I1.1`（名称 / 金额 / 截止 / 可见 / 关联账户）· `AC-I1.2`（**截止日可留空**）·
 * `AC-I4.3`（中性语气）· `DR-050` D3（账户可以先不绑）
 *
 * ## 🔴 三条端侧纪律
 *
 * 1. **校验走共享内核** `checkGoalShape()`（经 `features/goal.ts` 的 `goalDraftErrors`）——
 *    端侧只有即时提示，**最终裁决在服务端**；
 * 2. **截止日可以空着**：空串在提交时转 `null`（`DR-050` 契约第 2 条：空串不是"没截止日"的合法表达）；
 * 3. **编辑态不发整份**：由 `buildUpdateGoalRequest()` 剔掉没改的键（铁律 63）。
 */

import { Button, Chip, Stack, TextField, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import type { AccountView } from '@jiaying/shared';
import {
  GOAL_MAX_PER_LEDGER,
  type GoalDraft,
  type GoalDraftErrors,
} from '../features/goal.js';
import { AccountPicker } from './AccountPicker.js';

export interface GoalFormProps {
  draft: GoalDraft;
  errors: GoalDraftErrors;
  /** 可选账户（同币种；🔴 端侧只筛"可见的"，不判业务） */
  accounts: readonly AccountView[];
  /** `'create'` = 新建（标题不同，且允许"先不绑账户"） */
  mode: 'create' | 'edit';
  /** 编辑态且已有攒钱记录 ⇒ 账户选择被锁（服务端也会拒 —— 这是**提前告知**，不是替代） */
  accountLocked: boolean;
  busy: boolean;
  onChange: (next: GoalDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function GoalForm({
  draft,
  errors,
  accounts,
  mode,
  accountLocked,
  busy,
  onChange,
  onSubmit,
  onCancel,
}: GoalFormProps): ReactElement {
  const patch = (part: Partial<GoalDraft>): void => onChange({ ...draft, ...part });

  return (
    <Stack spacing={1.5}>
      <Typography variant="h6">{mode === 'create' ? '新建储蓄目标' : '编辑储蓄目标'}</Typography>
      <Typography variant="body2" color="text.secondary">
        进度只看「攒进来的钱」；关联账户只是<b>标记</b>，不会动它的真实余额，也不重复计入净资产。
      </Typography>

      <TextField
        label="目标名称"
        value={draft.name}
        onChange={(e) => patch({ name: e.target.value })}
        error={errors.name !== undefined}
        helperText={errors.name ?? '如「日本旅行」「家庭应急金」'}
        inputProps={{ 'data-testid': 'goal-name', maxLength: 30 }}
        size="small"
        fullWidth
      />

      <TextField
        label="目标金额（元）"
        value={draft.amountText}
        onChange={(e) => patch({ amountText: e.target.value })}
        error={errors.target !== undefined}
        helperText={errors.target ?? '整数分精度；可以改，进度会按新目标重算'}
        inputProps={{ 'data-testid': 'goal-amount', inputMode: 'decimal' }}
        size="small"
        fullWidth
      />

      <TextField
        label="截止日期（可留空）"
        type="date"
        value={draft.dueDate}
        onChange={(e) => patch({ dueDate: e.target.value })}
        error={errors.dueDate !== undefined}
        helperText={errors.dueDate ?? '留空 ⇒ 不判节奏（应急金这类目标本来就没有截止日）'}
        inputProps={{ 'data-testid': 'goal-due' }}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
      />

      {/*
        🆕 批 `115`（T4）：原生 `select` → 可键入拼音的选择器。
        🔴 `allowEmpty` 保住旧语义「（暂不关联）」= 可以先不绑账户（`DR-050` D3）；
        🔴 候选集合与顺序**一个字没动**（仍是 `accounts` 原样），只换控件。
        🔴 `testId` 保留 `goal-account`。
      */}
      <AccountPicker
        label="关联账户（可留空）"
        accounts={accounts.map((a) => ({ id: a.id, name: a.name }))}
        value={draft.accountId}
        onChange={(id) => patch({ accountId: id })}
        disabled={accountLocked}
        allowEmpty
        testId="goal-account"
        size="small"
        helperText={
          accountLocked
            ? '这个目标已有攒钱记录，换绑会让历史记录失去归属（进度归零）；如需更换请先全部取出'
            : '留空 ⇒ 第一次攒钱时再选（选一次就记住）'
        }
      />

      {/* 🆕 自动攒（`DR-052` D4）：执行入口是 F3 发薪日工作流，这里是**配置** */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          label="自动攒"
          value={draft.autoSaveMode}
          onChange={(e) => {
            const mode = e.target.value as GoalDraft['autoSaveMode'];
            // 从「不自动攒」切到别的模式时把额度清空，让用户从空白开始填
            onChange({ ...draft, autoSaveMode: mode, autoSaveText: mode === 'off' ? '' : draft.autoSaveText });
          }}
          SelectProps={{ native: true }}
          inputProps={{ 'data-testid': 'goal-auto-save-mode' }}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <option value="off">不自动攒</option>
          <option value="fixed">每期固定额</option>
          <option value="percent">按比例</option>
        </TextField>

        {draft.autoSaveMode !== 'off' && (
          <TextField
            label={draft.autoSaveMode === 'fixed' ? '每期攒（元）' : '比例（%）'}
            value={draft.autoSaveText}
            onChange={(e) => onChange({ ...draft, autoSaveText: e.target.value })}
            error={errors.autoSave !== undefined}
            helperText={errors.autoSave ?? (draft.autoSaveMode === 'fixed' ? '每次发薪攒这么多' : '每次发薪按实发的这个比例攒')}
            inputProps={{
              'data-testid': 'goal-auto-save-value',
              inputMode: draft.autoSaveMode === 'fixed' ? 'decimal' : 'numeric',
            }}
            size="small"
            sx={{ width: 180 }}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <Chip size="small" variant="outlined" label={`单账本上限 ${String(GOAL_MAX_PER_LEDGER)} 个`} />
        <Button onClick={onCancel} disabled={busy} data-testid="goal-cancel">
          取消
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="goal-submit">
          {mode === 'create' ? '建目标' : '保存'}
        </Button>
      </Stack>
    </Stack>
  );
}
